import { useEffect, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, ShieldCheck, Phone, Clock } from "lucide-react";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";
import { SERVICES } from "@/data/services";

const COUNTIES = {
  carlow:    { name: "Carlow",    region: "Leinster", anchor: "South-East" },
  cavan:     { name: "Cavan",     region: "Ulster",   anchor: "Border" },
  clare:     { name: "Clare",     region: "Munster",  anchor: "Mid-West" },
  cork:      { name: "Cork",      region: "Munster",  anchor: "South-West" },
  donegal:   { name: "Donegal",   region: "Ulster",   anchor: "Northern" },
  dublin:    { name: "Dublin",    region: "Leinster", anchor: "Capital" },
  galway:    { name: "Galway",    region: "Connacht", anchor: "Western" },
  kerry:     { name: "Kerry",     region: "Munster",  anchor: "South-West" },
  kildare:   { name: "Kildare",   region: "Leinster", anchor: "Greater Dublin" },
  kilkenny:  { name: "Kilkenny",  region: "Leinster", anchor: "South-East" },
  laois:     { name: "Laois",     region: "Leinster", anchor: "Midlands" },
  leitrim:   { name: "Leitrim",   region: "Connacht", anchor: "North-West" },
  limerick:  { name: "Limerick",  region: "Munster",  anchor: "Mid-West" },
  longford:  { name: "Longford",  region: "Leinster", anchor: "Midlands" },
  louth:     { name: "Louth",     region: "Leinster", anchor: "Border" },
  mayo:      { name: "Mayo",      region: "Connacht", anchor: "Western" },
  meath:     { name: "Meath",     region: "Leinster", anchor: "Greater Dublin" },
  monaghan:  { name: "Monaghan",  region: "Ulster",   anchor: "Border" },
  offaly:    { name: "Offaly",    region: "Leinster", anchor: "Midlands" },
  roscommon: { name: "Roscommon", region: "Connacht", anchor: "Midlands" },
  sligo:     { name: "Sligo",     region: "Connacht", anchor: "North-West" },
  tipperary: { name: "Tipperary", region: "Munster",  anchor: "Mid-West" },
  waterford: { name: "Waterford", region: "Munster",  anchor: "South-East" },
  westmeath: { name: "Westmeath", region: "Leinster", anchor: "Midlands" },
  wexford:   { name: "Wexford",   region: "Leinster", anchor: "South-East" },
  wicklow:   { name: "Wicklow",   region: "Leinster", anchor: "Greater Dublin" },
};

export default function AreaLanding() {
  const { slug } = useParams();
  const data = COUNTIES[slug?.toLowerCase()];

  useEffect(() => {
    if (data) {
      document.title = `Commercial Cleaning Services ${data.name} · AGS Ireland`;
      const meta = document.querySelector('meta[name="description"]');
      const desc = `Family-owned commercial & industrial cleaning across ${data.name} (${data.region}). Fully insured, RAMS & SDS ready. Same blue vest, same standards.`;
      if (meta) meta.setAttribute("content", desc);
    }
  }, [data]);

  const featuredServices = useMemo(() => SERVICES, []);

  if (!data) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A] antialiased" data-testid={`area-page-${slug}`}>
      <Navigation />
      <main className="pt-24">
        {/* Hero */}
        <header className="container-ags py-24 md:py-36 border-b border-[#E5EBF2]">
          <Link to="/" className="inline-flex items-center gap-2 text-[#475569] hover:text-[#0E2A57] font-mono text-[10px] uppercase tracking-[0.22em] mb-10">
            ← Back to home
          </Link>
          <div className="flex items-center gap-3 mb-6">
            <MapPin size={14} className="text-[#2BB6E5]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
              {data.region} · {data.anchor}
            </span>
          </div>
          <h1 className="font-heading font-extrabold text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.035em] text-[#050B14] max-w-4xl">
            Commercial Cleaning Services in <span className="italic-display font-normal text-[#0E2A57]">{data.name}</span>.
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#475569] leading-relaxed max-w-2xl">
            AGS Cleaning Services delivers professional cleaning across {data.name} and the wider {data.region} region —
            same family-owned standards, fully insured, RAMS &amp; SDS ready on every site.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to={`/quote?location=${encodeURIComponent(data.name)}`}
              data-testid="area-cta-quote"
              className="group inline-flex items-center gap-3 bg-[#0E2A57] text-white hover:bg-[#050B14] px-10 py-5 font-semibold tracking-wide transition-colors"
            >
              Request a quote for {data.name} <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <a href="tel:+353873816923" className="inline-flex items-center gap-3 border border-[#0E2A57] text-[#0E2A57] hover:bg-[#0E2A57] hover:text-white px-10 py-5 font-semibold tracking-wide transition-all">
              <Phone size={16} /> +353 87 381 6923
            </a>
          </div>
        </header>

        {/* Trust grid */}
        <section className="bg-[#F4F8FC] py-16 md:py-24 border-b border-[#E5EBF2]">
          <div className="container-ags">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#0E2A57]/15 border border-[#0E2A57]/15">
              {[
                { k: "Fully Insured", v: "Public & Employer's Liability" },
                { k: "RAMS + SDS", v: "Written procedures on every site" },
                { k: "24/7", v: `Emergency response across ${data.name}` },
                { k: "Same crew", v: "Every visit, same faces" },
              ].map((s, i) => (
                <div key={i} className="bg-white p-6 md:p-8">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/55">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="mt-3 font-heading font-extrabold text-2xl md:text-3xl tracking-[-0.02em] text-[#050B14] leading-tight">
                    {s.k}
                  </div>
                  <div className="mt-2 text-sm text-[#475569]">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services in this county */}
        <section className="bg-white py-24 md:py-36">
          <div className="container-ags">
            <div className="max-w-2xl mb-12">
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-16 bg-[#2BB6E5]" />
                <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
                  Services delivered in {data.name}
                </span>
              </div>
              <h2 className="font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-[-0.035em] text-[#050B14]">
                Every service we offer — delivered across {data.name}.
              </h2>
              <p className="mt-6 text-base md:text-lg text-[#475569] leading-relaxed">
                AGS delivers the full portfolio of commercial &amp; industrial cleaning programmes
                in {data.name} and the surrounding {data.region} region.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#0E2A57]/15 border border-[#0E2A57]/15">
              {featuredServices.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`area-service-${s.slug}`}
                  className="bg-white p-6 md:p-8 group hover:bg-[#F4F8FC] transition-colors flex flex-col"
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#2BB6E5] font-medium">
                    {s.no || "Service"}
                  </div>
                  <div className="mt-3 font-heading font-bold text-lg text-[#050B14] tracking-tight">
                    {s.title}
                  </div>
                  <div className="mt-2 text-sm text-[#475569] leading-relaxed line-clamp-3 flex-1">
                    {s.summary}
                  </div>
                  <div className="mt-4 inline-flex items-center gap-2 text-[#0E2A57] font-mono text-[10px] uppercase tracking-[0.22em] group-hover:gap-3 transition-all">
                    Read more <ArrowRight size={11} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Local trust strip */}
        <section className="bg-[#050B14] text-white py-24 md:py-36">
          <div className="container-ags grid md:grid-cols-12 gap-10">
            <div className="md:col-span-7">
              <div className="flex items-center gap-3 mb-6">
                <ShieldCheck size={16} className="text-[#2BB6E5]" />
                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#2BB6E5] font-medium">
                  Why {data.name} chooses AGS
                </span>
              </div>
              <h2 className="font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-[-0.03em]">
                Same crew, same standards — every shift, every site in {data.name}.
              </h2>
              <p className="mt-6 text-lg text-white/70 leading-relaxed max-w-xl">
                We don&apos;t subcontract. The blue vest your facilities manager sees on Monday morning is on our payroll, trained by our supervisors, and accountable to the Sweeney family.
              </p>
            </div>
            <div className="md:col-span-5 md:col-start-8 flex flex-col gap-4 text-white/80">
              <div className="border border-white/15 p-6">
                <Clock size={18} className="text-[#2BB6E5] mb-3" />
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/55">Response time</div>
                <div className="mt-1 font-heading font-bold text-xl">Under 24h across {data.name}</div>
              </div>
              <div className="border border-white/15 p-6">
                <ShieldCheck size={18} className="text-[#2BB6E5] mb-3" />
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/55">Insurance</div>
                <div className="mt-1 font-heading font-bold text-xl">Fully Insured</div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-white py-24 md:py-36 border-t border-[#E5EBF2]">
          <div className="container-ags max-w-3xl">
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-[-0.03em] text-[#050B14]">
              Ready to get a quote for {data.name}?
            </h2>
            <p className="mt-6 text-lg text-[#475569]">
              Tell us about your site — square footage, frequency, compliance needs — and we&apos;ll come back with a tailored proposal within one working day.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to={`/quote?location=${encodeURIComponent(data.name)}`}
                className="inline-flex items-center gap-3 bg-[#0E2A57] text-white hover:bg-[#050B14] px-10 py-5 font-semibold tracking-wide transition-colors"
              >
                Request quote <ArrowRight size={18} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 border border-[#0E2A57] text-[#0E2A57] hover:bg-[#0E2A57] hover:text-white px-10 py-5 font-semibold tracking-wide transition-all"
              >
                Talk to a person
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
